import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mic, MicOff, X, Phone, PhoneOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export default function AIWaiterVoiceChat({ tableId, restaurantId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [status, setStatus] = useState('Tap to start talking');
  const [transcript, setTranscript] = useState('');
  const [agentResponse, setAgentResponse] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [quickTips, setQuickTips] = useState([]);

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioWorkletRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const sourceNodeRef = useRef(null);
  const systemPromptRef = useRef('');
  const menuTextRef = useRef('');
  const firstMessageRef = useRef('');

  useEffect(() => { return () => cleanup(); }, []);
  useEffect(() => { loadDynamicContext(); }, []);
  useEffect(() => { if (isOpen && !isConnected && !isConnecting) startConversation(); }, [isOpen]);

  const loadDynamicContext = async () => {
    try {
      const res = await fetch(`${API}/elevenlabs/dynamic-context`);
      if (res.ok) {
        const data = await res.json();
        setRestaurantName(data.restaurant_name);
        setQuickTips(data.quick_tips);
        systemPromptRef.current = data.system_prompt;
        menuTextRef.current = data.menu_text;
        firstMessageRef.current = data.first_message;
        console.log('Loaded context:', { restaurant: data.restaurant_name, menuLength: data.menu_text?.length, firstMessage: data.first_message });
      }
    } catch (e) { console.error('Context load error:', e); }
  };

  const cleanup = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    mediaStreamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    setIsConnected(false);
    setIsConnecting(false);
    setIsSpeaking(false);
    setIsListening(false);
    audioQueueRef.current = [];
    isPlayingRef.current = false;
  }, []);

  const startConversation = async () => {
    try {
      setIsConnecting(true);
      setStatus('Connecting...');
      const configRes = await fetch(`${API}/elevenlabs/config`);
      if (!configRes.ok) throw new Error('Voice AI not configured');
      const urlRes = await fetch(`${API}/elevenlabs/signed-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table_id: tableId, restaurant_id: restaurantId })
      });
      if (!urlRes.ok) throw new Error('Failed to connect');
      const { signed_url, system_prompt, dynamic_variables } = await urlRes.json();
      systemPromptRef.current = system_prompt;
      if (dynamic_variables?.restaurant_name) setRestaurantName(dynamic_variables.restaurant_name);
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 }
      });
      connectWebSocket(signed_url);
    } catch (e) {
      console.error('Start error:', e);
      setStatus('Connection failed');
      toast.error(e.message);
      setIsConnecting(false);
    }
  };

  const connectWebSocket = (url) => {
    wsRef.current = new WebSocket(url);
    wsRef.current.onopen = () => {
      console.log('WebSocket connected to ElevenLabs');

      const metadataPayload = {
        type: "conversation_initiation_metadata",
        dynamic_variables: {
          table_id: tableId,
          restaurant_id: restaurantId
        }
      };

      console.log("👉 Sending custom metadata:", metadataPayload);
      wsRef.current.send(JSON.stringify(metadataPayload));

      setIsConnected(true);
      setIsConnecting(false);
      setIsListening(true);
      setStatus('Listening...');
      startAudioCapture();
    };


    wsRef.current.onmessage = (e) => handleMessage(e);
    wsRef.current.onerror = (err) => {
      console.error('WebSocket error:', err);
      toast.error('Connection error');
    };
    wsRef.current.onclose = (e) => {
      console.log('WebSocket closed:', e.code, e.reason);
      setStatus('Disconnected');
      cleanup();
    };
  };

  const handleMessage = async (event) => {
    try {
      const msg = JSON.parse(event.data);
      console.log('WS Message:', msg.type, msg);
      
      if (msg.type === 'conversation_initiation_metadata') {
        console.log('Conversation initialized:', msg);
        const conversationId = msg.conversation_initiation_metadata_event?.conversation_id;

        console.log("Conversation ID:", conversationId);

        if (!conversationId || !tableId) {
          console.warn("Missing conversationId or tableId");
          return;
        }

        fetch(`${API}/elevenlabs/conversation-context`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversation_id: conversationId,
            table_id: tableId
          })
        });

        
      } else if (msg.type === 'user_transcript') {
        const txt = msg.user_transcription_event?.user_transcript || '';
        if (txt) { 
          console.log('User said:', txt);
          setTranscript(txt); 
          setIsListening(true); 
          setIsSpeaking(false); 
          setStatus('Listening...'); 
        }
      } else if (msg.type === 'agent_response') {
        const txt = msg.agent_response_event?.agent_response || '';
        if (txt) { 
          console.log('Agent says:', txt);
          setAgentResponse(txt); 
          setIsSpeaking(true); 
          setIsListening(false); 
          setStatus('Speaking...'); 
        }
      } else if (msg.type === 'audio' && msg.audio_event?.audio_base_64) {
        await playAudio(msg.audio_event.audio_base_64);
      } else if (msg.type === 'interruption') {
        stopPlayback(); setIsSpeaking(false); setIsListening(true); setStatus('Listening...');
      } else if (msg.type === 'ping') {
        wsRef.current?.send(JSON.stringify({ type: 'pong', event_id: msg.ping_event?.event_id }));
      } else if (msg.type === 'agent_response_correction') {
        setAgentResponse(msg.agent_response_correction_event?.corrected_agent_response || '');
      }
    } catch (e) { console.error('Message error:', e); }
  };

  const startAudioCapture = async () => {
    if (!mediaStreamRef.current || !audioContextRef.current) return;
    try {
      await audioContextRef.current.audioWorklet.addModule(
        URL.createObjectURL(new Blob([`class P extends AudioWorkletProcessor{process(i){const s=i[0]?.[0];if(s){const a=new Int16Array(s.length);for(let j=0;j<s.length;j++){const v=Math.max(-1,Math.min(1,s[j]));a[j]=v<0?v*0x8000:v*0x7FFF}this.port.postMessage(a.buffer)}return true}}registerProcessor('p',P);`], { type: 'application/javascript' }))
      );
      const src = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      audioWorkletRef.current = new AudioWorkletNode(audioContextRef.current, 'p');
      let audioChunkCount = 0;
      audioWorkletRef.current.port.onmessage = (e) => {
        if (wsRef.current?.readyState === WebSocket.OPEN && !isMuted) {
          let b = ''; const u = new Uint8Array(e.data);
          for (let i = 0; i < u.length; i++) b += String.fromCharCode(u[i]);
          wsRef.current.send(JSON.stringify({ user_audio_chunk: btoa(b) }));
          audioChunkCount++;
          if (audioChunkCount % 50 === 0) console.log('Audio chunks sent:', audioChunkCount);
        }
      };
      src.connect(audioWorkletRef.current);
      audioWorkletRef.current.connect(audioContextRef.current.destination);
      setIsListening(true);
    } catch (e) { console.error('Audio error:', e); }
  };

  const playAudio = async (b64) => {
    if (!audioContextRef.current) return;
    try {
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const i16 = new Int16Array(bytes.buffer);
      const f32 = new Float32Array(i16.length);
      for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768.0;
      const buf = audioContextRef.current.createBuffer(1, f32.length, 16000);
      buf.getChannelData(0).set(f32);
      audioQueueRef.current.push(buf);
      if (!isPlayingRef.current) playNext();
    } catch (e) { console.error('Play error:', e); }
  };

  const playNext = () => {
    if (!audioQueueRef.current.length) {
      isPlayingRef.current = false; setIsSpeaking(false); setIsListening(true); setStatus('Listening...'); return;
    }
    isPlayingRef.current = true;
    const buf = audioQueueRef.current.shift();
    if (audioContextRef.current) {
      const src = audioContextRef.current.createBufferSource();
      src.buffer = buf;
      src.connect(audioContextRef.current.destination);
      src.onended = playNext;
      src.start();
      sourceNodeRef.current = src;
    }
  };

  const stopPlayback = () => {
    audioQueueRef.current = [];
    try { sourceNodeRef.current?.stop(); } catch {}
    isPlayingRef.current = false;
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    mediaStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = isMuted; });
    toast.success(isMuted ? 'Mic on' : 'Mic muted');
  };

  const endCall = () => { cleanup(); setIsOpen(false); setTranscript(''); setAgentResponse(''); setStatus('Tap to start'); };

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-green-500 to-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform flex items-center gap-2">
          <Mic className="w-6 h-6" />
          <span className="font-semibold">{restaurantName ? `Talk to ${restaurantName} AI` : 'Talk to AI Waiter'}</span>
        </button>
      )}
      <Dialog open={isOpen} onOpenChange={(o) => { if (!o) endCall(); else setIsOpen(true); }}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {restaurantName ? `${restaurantName} AI Waiter` : 'AI Waiter'}
              </DialogTitle>
              <button onClick={endCall} className="text-white hover:bg-white/20 rounded-full p-1.5"><X className="w-5 h-5" /></button>
            </div>
          </DialogHeader>
          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all ${isConnecting ? 'bg-yellow-100' : isSpeaking ? 'bg-blue-100 animate-pulse' : isListening ? 'bg-green-100' : 'bg-gray-100'}`}>
                {isConnecting ? <Loader2 className="w-12 h-12 text-yellow-600 animate-spin" /> : isSpeaking ? (
                  <div className="flex items-center gap-1">{[...Array(4)].map((_, i) => <div key={i} className="w-2 bg-blue-500 rounded-full animate-bounce" style={{ height: `${20 + Math.random() * 20}px`, animationDelay: `${i * 0.15}s` }} />)}</div>
                ) : isListening ? <Mic className="w-12 h-12 text-green-600" /> : <MicOff className="w-12 h-12 text-gray-400" />}
                {(isListening || isSpeaking) && <><div className={`absolute inset-0 rounded-full border-4 animate-ping opacity-30 ${isSpeaking ? 'border-blue-400' : 'border-green-400'}`} /><div className={`absolute inset-[-8px] rounded-full border-2 animate-pulse opacity-20 ${isSpeaking ? 'border-blue-300' : 'border-green-300'}`} /></>}
              </div>
              <p className="mt-4 text-center font-medium text-gray-700">{status}</p>
            </div>
            {(transcript || agentResponse) && (
              <div className="mb-6 space-y-3 max-h-40 overflow-y-auto">
                {transcript && <div className="bg-green-50 p-3 rounded-lg"><p className="text-xs text-green-600 font-medium mb-1">You:</p><p className="text-sm text-gray-800">{transcript}</p></div>}
                {agentResponse && <div className="bg-blue-50 p-3 rounded-lg"><p className="text-xs text-blue-600 font-medium mb-1">AI Waiter:</p><p className="text-sm text-gray-800">{agentResponse}</p></div>}
              </div>
            )}
            <div className="flex justify-center gap-4">
              {isConnected && <Button onClick={toggleMute} variant={isMuted ? 'destructive' : 'outline'} size="lg" className="rounded-full w-14 h-14">{isMuted ? <MicOff size={22} /> : <Mic size={22} />}</Button>}
              <Button onClick={endCall} variant="destructive" size="lg" className="rounded-full w-14 h-14"><PhoneOff size={22} /></Button>
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 font-medium mb-2">Quick Tips:</p>
              <ul className="text-xs text-gray-500 space-y-1">
                {quickTips.length > 0 ? quickTips.map((t, i) => <li key={i}>• {t}</li>) : <><li>• Say "What is on the menu?"</li><li>• Ask "What do you recommend?"</li><li>• Say "That is all" when done</li></>}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
